'use client';

import type { PreparedNangoPopup } from '@/data/nango/nango-popup';
import type { IntegrationConnectionState } from '@/lib/integrations/auth-status';
import type { NangoConnector } from '@/lib/nango/config';
import type { RealtimeEventKey } from '@/lib/realtime/types';
import type { AuthOptions } from '@nangohq/frontend';
import Nango from '@nangohq/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { prepareNangoPopup } from '@/data/nango/nango-popup';
import { onboardingStatusQueryKey } from '@/data/onboarding/use-onboarding-status';
import { clientLogger } from '@/lib/client-logger';
import { useRealtime } from '@/lib/hooks/use-realtime';
import { sanitizeNangoError } from '@/lib/integrations/nango-proxy-error';
import { captureException } from '@/lib/sentry';
import { createClient } from '@/utils/supabase/client';
export type NangoIntegrationOptions<TConnection = unknown> = {
  /** Provider name for Nango auth (e.g., 'slack', 'github') */
  provider: NangoConnector;
  /** API endpoint to get connection status */
  getConnectionUrl: string;
  /** API endpoint to delete/disconnect */
  disconnectUrl: string;
  /** Realtime event key for connection updates */
  realtimeEventKey: RealtimeEventKey;
  /** React Query key for this provider */
  queryKey: readonly string[];
  /** Optional auth options to pass to nango.auth() */
  authOptions?: AuthOptions;
  /** Optional installation ID for installation-scoped Nango sessions */
  installationId?: number;
  /** Optional endpoint to reconcile provider state immediately after Nango auth completes */
  syncAfterAuthUrl?: string;
  /** Whether the query should be enabled (defaults to true) */
  enabled?: boolean;
  /** Optional provider-specific connection-state mapper */
  getConnectionState?: (connection: TConnection | null | undefined) => IntegrationConnectionState;
  /** Callback functions for provider-specific logic */
  callbacks?: {
    onConnectAttempt?: () => void;
    onConnectSuccess?: (connection: TConnection | null) => void;
    onConnectError?: (error: Error, reason?: string) => void;
    onDisconnectSuccess?: () => void;
    onDisconnectError?: (error: Error) => void;
    onPollingTimeout?: (attempts: number) => void;
  };
  /** Optional callback for handling realtime events generically */
  onRealtimeEventAction?: (payload: any, helpers: {
    queryClient: ReturnType<typeof useQueryClient>;
    queryKey: readonly string[];
    setIsPolling: (v: boolean) => void;
  }) => void;
};
export function useNangoIntegration<TConnection = unknown>(options: NangoIntegrationOptions<TConnection>) {
  const {
    provider,
    getConnectionUrl,
    disconnectUrl,
    realtimeEventKey,
    queryKey,
    authOptions,
    callbacks,
    installationId,
    syncAfterAuthUrl,
    enabled = true,
    getConnectionState = value => value ? 'connected' : 'disconnected'
  } = options;
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState(false);
  const [userId, setUserId] = useState<string>();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectionConfirmed = (value_0: unknown) => getConnectionState(value_0 as TConnection | null | undefined) === 'connected';

  // Fetch current user ID for realtime channel subscription
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({
      data: {
        user
      }
    }) => {
      setUserId(user?.id);
    }).catch(err => {
      console.error(`Failed to get user for realtime ${provider} subscription:`, sanitizeNangoError(err));
    });
  }, [provider]);

  // Query for the connection status
  const {
    data: connection,
    isLoading: isLoadingConnection
  } = useQuery<TConnection | null>({
    queryKey,
    enabled,
    queryFn: async () => {
      const response = await fetch(getConnectionUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${provider} connection status`);
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      return data.connection as TConnection | null;
    },
    staleTime: 0
  });

  // Listen for realtime connection update events
  useRealtime({
    eventKey: realtimeEventKey,
    id: userId ?? '',
    enabled: !!userId,
    onEvent: payload => {
      if (options.onRealtimeEventAction) {
        options.onRealtimeEventAction(payload, {
          queryClient,
          queryKey,
          setIsPolling
        });
      } else {
        // Default: just invalidate the query and stop polling
        queryClient.invalidateQueries({
          queryKey
        });
        setIsPolling(false);
      }
    }
  });

  // Mutation for connecting to the provider
  const {
    mutate: connectMutation,
    isPending: isConnecting
  } = useMutation({
    mutationFn: async (params: {
      popupWindow?: Window | null;
      cleanup?: () => void;
    }) => {
      const {
        popupWindow,
        cleanup
      } = params;
      const url = installationId ? `/api/nango/session?installationId=${installationId}` : '/api/nango/session';
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider
        })
      });
      if (!res.ok) {
        const errorBody = await res.text();
        console.error('Failed to create Nango session:', res.status, errorBody);
        // Close the popup if we opened one
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
        if (cleanup) {
          cleanup();
        }
        throw new Error(`Failed to create Nango session (status ${res.status})`);
      }
      const {
        data: {
          token: sessionToken
        }
      } = await res.json();
      if (!sessionToken) {
        console.error('No token found directly in Nango session response');
        // Close the popup if we opened one
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
        if (cleanup) {
          cleanup();
        }
        throw new Error('No session token received');
      }
      return {
        sessionToken,
        cleanup
      };
    },
    onError: (error, variables) => {
      setIsPolling(false);
      if (variables?.cleanup) {
        variables.cleanup();
      }
      const errorInstance = error instanceof Error ? error : new Error(String(error));
      captureException(errorInstance);
      callbacks?.onConnectError?.(errorInstance, 'auth_error');
    },
    onSuccess: async ({
      sessionToken: sessionToken_0,
      cleanup: cleanup_0
    }) => {
      try {
        if (!sessionToken_0) {
          throw new Error('No session token received');
        }
        const nango = new Nango({
          connectSessionToken: sessionToken_0
        });

        // Note: If cleanup is provided, the monkey-patch was already set up in connect()

        try {
          await nango.auth(provider, authOptions);
        } catch (authError) {
          setIsPolling(false);
          console.error('Nango auth error:', sanitizeNangoError(authError));

          // Check specifically for popup blocked error
          if (authError instanceof Error && authError.message.includes('Modal blocked by browser')) {
            callbacks?.onConnectError?.(authError, 'popup_blocked');
            captureException(authError, {
              tags: {
                source: `${provider}_popup_blocked`
              }
            });
            return;
          }
          // Re-throw other errors to be caught by the outer catch block
          throw authError;
        } finally {
          // Always run cleanup if provided
          if (cleanup_0) {
            cleanup_0();
          }
        }
        if (syncAfterAuthUrl) {
          try {
            const syncResponse = await fetch(syncAfterAuthUrl, {
              method: 'POST'
            });
            if (syncResponse.ok) {
              const syncData = await syncResponse.json();
              const syncedConnection = syncData.connection as TConnection | null;
              if (isConnectionConfirmed(syncedConnection)) {
                queryClient.setQueryData(queryKey, syncedConnection);
                setIsPolling(false);
                callbacks?.onConnectSuccess?.(syncedConnection);
                // Invalidate onboarding status when integration is connected
                if (installationId) {
                  queryClient.invalidateQueries({
                    queryKey: onboardingStatusQueryKey(installationId)
                  });
                }
                return;
              }
            } else {
              const errorBody_0 = await syncResponse.text().catch(() => '');
              clientLogger.warn(`Failed to sync ${provider} connection after auth`, {
                body: errorBody_0,
                status: syncResponse.status
              });
            }
          } catch (syncError) {
            captureException(syncError instanceof Error ? syncError : new Error(String(syncError)), {
              extra: {
                provider,
                syncAfterAuthUrl
              }
            });
          }
        }
        setIsPolling(true);

        // Poll for connection confirmation
        let attempts = 0;
        const maxAttempts = 25;
        const pollConnection = async () => {
          attempts++;
          await queryClient.refetchQueries({
            queryKey
          });
          const data_0 = queryClient.getQueryData(queryKey);
          if (isConnectionConfirmed(data_0)) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            pollingIntervalRef.current = null;
            setIsPolling(false);
            callbacks?.onConnectSuccess?.(data_0 as TConnection | null);
            // Invalidate onboarding status when integration is connected
            if (installationId) {
              queryClient.invalidateQueries({
                queryKey: onboardingStatusQueryKey(installationId)
              });
            }
          } else if (attempts >= maxAttempts) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            pollingIntervalRef.current = null;
            setIsPolling(false);
            callbacks?.onPollingTimeout?.(attempts);
            captureException(new Error(`Failed to confirm ${provider} connection after polling timeout`), {
              extra: {
                attempts,
                provider
              }
            });
          }
        };
        pollingIntervalRef.current = setInterval(() => {
          pollConnection().catch((pollError: unknown) => {
            captureException(pollError instanceof Error ? pollError : new Error(String(pollError)), {
              extra: {
                attempts,
                provider
              }
            });
          });
        }, 1500); // 1.5 seconds interval
      } catch (err_0) {
        setIsPolling(false);
        if (cleanup_0) {
          cleanup_0();
        }
        const errorToCapture = err_0 instanceof Error ? err_0 : new Error(String(err_0));
        captureException(errorToCapture);
        callbacks?.onConnectError?.(errorToCapture, 'auth_error');
      }
    }
  });

  // Mutation for disconnecting from the provider
  const {
    mutate: disconnect,
    isPending: isDisconnecting
  } = useMutation({
    mutationFn: async () => {
      const response_0 = await fetch(disconnectUrl, {
        method: 'DELETE'
      });
      if (!response_0.ok) {
        const error_0 = await response_0.json();
        throw new Error(error_0.error || `Failed to disconnect ${provider}`);
      }
      return response_0.json();
    },
    onMutate: async () => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey
      });

      // Snapshot the previous value
      const previousConnection = queryClient.getQueryData(queryKey);

      // Optimistically set connection to null
      queryClient.setQueryData(queryKey, null);

      // Return context with the snapshotted value
      return {
        previousConnection
      };
    },
    onError: (error_1, _variables, context) => {
      // On error, revert back to the previous state
      if (context?.previousConnection) {
        queryClient.setQueryData(queryKey, context.previousConnection);
      }
      const errorInstance_0 = error_1 instanceof Error ? error_1 : new Error(String(error_1));
      captureException(errorInstance_0);
      callbacks?.onDisconnectError?.(errorInstance_0);
    },
    onSuccess: () => {
      callbacks?.onDisconnectSuccess?.();
      // Invalidate and refetch the connection query to update UI
      queryClient.invalidateQueries({
        queryKey
      });
      // Invalidate onboarding status when integration is disconnected
      if (installationId) {
        queryClient.invalidateQueries({
          queryKey: onboardingStatusQueryKey(installationId)
        });
      }
    }
  });

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);
  const connectWithPreparedPopup = (preparedPopup: PreparedNangoPopup) => {
    connectMutation({
      popupWindow: preparedPopup.popupWindow,
      cleanup: preparedPopup.cleanup
    });
  };

  // Opens immediately to preserve the browser user gesture before Nango auth.
  const connect = () => {
    callbacks?.onConnectAttempt?.();
    connectWithPreparedPopup(prepareNangoPopup());
  };
  return {
    connection,
    connectionState: getConnectionState(connection),
    isLoadingConnection,
    connect,
    connectWithPreparedPopup,
    isConnecting,
    disconnect,
    isDisconnecting,
    isPolling
  };
}